"use client";

import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { type ChangeEvent, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Button,
  Checkbox,
  CloseIcon,
  Group,
  InfoIcon,
  Loader,
  Modal,
  PlaceIcon,
  Stack,
  Text,
} from "@/components/ui/elements";
import { InputField } from "@/components/ui/parts";

import { useAddressRegistrationModal } from "@/hooks/localStorage/useAddressRegistrationModal";
import { useAddressByZipCode } from "@/hooks/zip";
import { i18n, infoMessage } from "@/i18n";
import {
  buildingValidation,
  cityValidation,
  prefectureValidation,
  streetValidation,
  zipCodeValidation,
} from "@/types/validation";
import { showNotification } from "@/util/userNotifications";

import { registerAddressMutation } from "./_graphql";

const schema = z.object({
  zipCode: zipCodeValidation(),
  prefecture: prefectureValidation(),
  city: cityValidation(),
  street: streetValidation(),
  building: z.union([buildingValidation(), z.undefined()]),
});

type Schema = z.infer<typeof schema>;

type Props = {
  opened: boolean;
  onClose: () => void;
};

export function AddressRegistrationModal({ opened, onClose }: Props) {
  const [doNotShow, setDoNotShow] = useState(false);
  const { getAddress, loading: isSearchingAddress } = useAddressByZipCode();
  const { setHideModal, setTemporaryHide } = useAddressRegistrationModal();
  const [mutation, { loading: mutationLoading }] = useMutation(
    registerAddressMutation,
  );

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<Schema>({
    mode: "onSubmit",
    defaultValues: {
      zipCode: "",
      prefecture: "",
      city: "",
      street: "",
      building: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: Schema) => {
    await mutation({
      variables: {
        input: {
          postalCode: data.zipCode,
          prefecture: data.prefecture,
          city: data.city,
          street: data.street,
          building: data.building || null,
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (data) => {
        const res = data.updateAddress;
        if (!res) return;
        showNotification({ type: "info", message: infoMessage("addAddress") });
        onClose();
      },
    });
  };

  const handleClose = () => {
    if (doNotShow) {
      // 「今後表示しない」がチェックされている場合は永続的に非表示
      setHideModal(true);
    } else {
      // チェックされていない場合は1日非表示
      setTemporaryHide();
    }
    onClose();
  };

  const handleZipCodeChange = async () => {
    const zipCode = getValues("zipCode");

    // 入力チェック
    if (!zipCode) {
      showNotification({
        type: "error",
        title: "郵便番号を入力してください",
        message: "7桁の郵便番号を入力してください",
        color: "orange",
      });
      return;
    }

    if (zipCode.length !== 7) {
      showNotification({
        type: "error",
        title: "郵便番号が正しくありません",
        message: "ハイフンなしの7桁で入力してください",
        color: "orange",
      });
      return;
    }
    try {
      const { address } = await getAddress(zipCode);
      if (address) {
        setValue("prefecture", address.prefecture);
        setValue("city", address.city);
        setValue("street", address.street);
        showNotification({
          type: "info",
          title: "住所を自動入力しました",
          message: "郵便番号から住所を取得しました",
          color: "blue",
        });
      } else {
        showNotification({
          type: "error",
          title: "住所が見つかりません",
          message: "郵便番号を確認してください",
          color: "orange",
        });
      }
    } catch (error) {
      console.error("住所検索エラー:", error);
      showNotification({
        type: "error",
        title: "検索に失敗しました",
        message: "もう一度お試しください",
        color: "red",
      });
    }
  };

  return (
    <Modal
      opened={opened}
      close={onClose}
      centered
      size="md"
      padding="xs"
      withCloseButton={false}
      styles={{
        body: { padding: "12px", position: "relative" },
      }}
    >
      {/* カスタム閉じるボタン */}
      <button
        onClick={handleClose}
        disabled={mutationLoading}
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          zIndex: 1000,
          background: "transparent",
          border: "none",
          cursor: mutationLoading ? "not-allowed" : "pointer",
          padding: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          opacity: mutationLoading ? 0.5 : 1,
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          if (!mutationLoading)
            e.currentTarget.style.backgroundColor = "#f1f3f5";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
        aria-label="閉じる"
      >
        <CloseIcon size={20} color="#868e96" />
      </button>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="sm">
          {/* ヘッダー - さらにコンパクト化 */}
          <Stack gap={2} align="center" pt="xs">
            <Group gap="xs" wrap="nowrap">
              <PlaceIcon size={20} color="orange" />
              <Text size="md" fw={700} c="orange.7" style={{ margin: 0 }}>
                住所登録
              </Text>
            </Group>
            <Text ta="center" c="dimmed" size="xs" style={{ fontSize: "11px" }}>
              お仕事に応募するには住所登録が必要です
            </Text>
          </Stack>

          {/* メリット説明 - 1行にコンパクト化 */}
          <div
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              padding: "6px 10px",
              border: "1px solid #dee2e6",
            }}
          >
            <Group gap={6} wrap="nowrap">
              <InfoIcon size={14} color="#495057" style={{ flexShrink: 0 }} />
              <Text
                size="xs"
                c="gray.7"
                style={{ lineHeight: 1.3, fontSize: "11px" }}
              >
                登録すると住所から近いお仕事を表示できます
              </Text>
            </Group>
          </div>

          {/* フォームセクション - コンパクト化 */}
          <Stack gap="sm">
            {/* 郵便番号入力 - 簡素化 */}
            <Group align="flex-end" gap="xs" wrap="nowrap">
              <InputField
                style={{ flex: 1, maxWidth: "180px" }}
                label={i18n("zipCode")}
                name="zipCode"
                control={control}
                withAsterisk
                placeholder="1234567"
                description="ハイフンなし7桁"
                disabled={isSearchingAddress}
                size="sm"
              />
              <Button
                type="button"
                onClick={handleZipCodeChange}
                disabled={isSearchingAddress}
                size="xs"
                variant="light"
                color="blue"
                style={{
                  height: "36px",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                  flexShrink: 0,
                }}
              >
                {isSearchingAddress ? (
                  <Group gap={4}>
                    <Loader size="xs" />
                    <span>検索中</span>
                  </Group>
                ) : (
                  "住所検索"
                )}
              </Button>
            </Group>

            {/* 住所入力 - コンパクト化 */}
            <Stack gap="xs">
              <Group grow>
                <InputField
                  label={i18n("prefecture")}
                  name="prefecture"
                  control={control}
                  withAsterisk
                  placeholder="千葉県"
                  size="sm"
                  disabled={isSearchingAddress}
                />
                <InputField
                  label={i18n("city")}
                  name="city"
                  control={control}
                  withAsterisk
                  placeholder="千葉市"
                  size="sm"
                  disabled={isSearchingAddress}
                />
              </Group>
              <InputField
                label={i18n("street")}
                name="street"
                control={control}
                withAsterisk
                placeholder="中央区市場町１−１"
                size="sm"
                disabled={isSearchingAddress}
              />
              <InputField
                label={i18n("building")}
                name="building"
                control={control}
                placeholder="建物名・部屋番号（任意）"
                size="sm"
                disabled={isSearchingAddress}
              />
            </Stack>
          </Stack>

          {/* チェックボックスとボタンセクション */}
          <Stack gap="xs" pt="xs">
            <Checkbox
              label="今後表示しない"
              checked={doNotShow}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setDoNotShow(event.currentTarget.checked)
              }
              size="sm"
            />

            <Button
              color="orange"
              type="submit"
              size="md"
              loading={mutationLoading}
              fullWidth
            >
              {mutationLoading ? "登録中..." : "登録する"}
            </Button>
            <Button
              variant="subtle"
              color="gray"
              onClick={handleClose}
              size="xs"
              disabled={mutationLoading}
              fullWidth
            >
              後で登録
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
